import { createContext, useContext, useMemo, useState, ReactNode } from "react";

export type BannerName = "LANGUAGE" | "CONNECTION" | "AUTOFILL_FAILED";

export type BannersVisibilityContextValue = {
  visibility: Record<BannerName, boolean>;
  close: (bannerName: BannerName) => void;
  open: (bannerName: BannerName) => void;
};

const BannersVisibilityContext = createContext<
  BannersVisibilityContextValue | undefined
>(undefined);

export type BannersVisibilityProviderProps = {
  children: ReactNode;
  initial: Record<BannerName, boolean>;
};

export const BannersVisibilityProvider = ({
  children,
  initial,
}: BannersVisibilityProviderProps) => {
  const [visibility, setVisibility] =
    useState<Record<BannerName, boolean>>(initial);

  const value = useMemo<BannersVisibilityContextValue>(
    () => ({
      visibility,
      close: (bannerName: BannerName) => {
        setVisibility((prev) => ({ ...prev, [bannerName]: false }));
      },
      open: (bannerName: BannerName) => {
        setVisibility((prev) => ({ ...prev, [bannerName]: true }));
      },
    }),
    [visibility],
  );

  return (
    <BannersVisibilityContext.Provider value={value}>
      {children}
    </BannersVisibilityContext.Provider>
  );
};

export const useBannersVisibility = ():
  | BannersVisibilityContextValue
  | undefined => useContext(BannersVisibilityContext);
